import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, CalendarPlus, ArrowLeft, ArrowRight } from "lucide-react";
import { api, absFile, evDate } from "@/lib/api";
import { themeFor } from "@/lib/themes";
import { EventDecor, Ticket, Asterisk, FloralVine } from "@/components/Motifs";
import { useGuest, invitedNames } from "@/lib/GuestContext";

export default function EventDetail() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const [all, setAll] = useState([]);
  const [missing, setMissing] = useState(false);
  const { guest } = useGuest();
  useEffect(() => {
    setE(null); setMissing(false);
    api.get(`/public/events/${slug}`).then((r) => setE(r.data)).catch(() => setMissing(true));
    api.get("/public/events").then((r) => setAll(r.data || []));
  }, [slug]);
  if (missing) return <Navigate to="/celebrations" replace />;
  if (!e) return <div className="py-32 text-center font-hand text-2xl">Setting the stage…</div>;

  const t = themeFor(slug);
  const dateObj = evDate(e.date);
  const niceDate = dateObj.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const idx = all.findIndex((x) => x.slug === slug);
  const next = all.length ? all[(idx + 1) % all.length] : null;
  const onLight = slug === "mehendi" || slug === "wedding";
  const names = invitedNames(guest, slug);

  const addToCalendar = () => {
    const start = e.date.replace(/-/g, "");
    const end = new Date(dateObj.getTime() + 86400000);
    const endStr = `${end.getFullYear()}${String(end.getMonth() + 1).padStart(2, "0")}${String(end.getDate()).padStart(2, "0")}`;
    const q = (s) => encodeURIComponent(s || "");
    window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${q(`${e.name} | Niyati & Girish`)}&dates=${start}/${endStr}&location=${q(e.venue)}&details=${q(e.description)}`, "_blank");
  };

  return (
    <div data-testid={`event-detail-${slug}`} style={{ background: t.bg, color: t.fg }} className="relative overflow-hidden">
      <EventDecor slug={slug} t={t} page />
      <section className="relative max-w-7xl mx-auto px-5 md:px-10 pt-14 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 min-w-0">
          <Link to="/celebrations" className="kicker inline-flex items-center gap-2 opacity-80 hover:opacity-100" data-testid="back-to-celebrations"><ArrowLeft size={14} /> All celebrations</Link>
          <div className="kicker mt-8" style={{ color: t.accent }}>{t.label}</div>
          <h1 className={`${slug === "reception" ? "text-4xl sm:text-5xl md:text-6xl lg:text-[5.25rem]" : "text-5xl sm:text-6xl md:text-8xl lg:text-[7.5rem]"} leading-[0.88] mt-3 break-words ${t.titleClass}`}>{e.name}</h1>
          <p className="font-display italic text-3xl md:text-4xl mt-6 leading-tight" style={{ color: t.accent }}>{e.headline || t.headline}</p>
          <p className="font-hand text-xl md:text-2xl mt-4 opacity-90">{e.tagline || t.tagline}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Ticket notch={t.bg} style={{ background: t.fg, color: t.bg }}>{dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</Ticket>
            <Ticket notch={t.bg} style={{ background: t.accent, color: slug === "reception" ? t.bg : "#FFF9F0" }}>{e.venue}</Ticket>
          </div>
        </div>
        <div className="lg:col-span-6 relative">
          <div className={`overflow-hidden h-[400px] md:h-[580px] ${t.imgClass} ${slug === "sangeet" ? "aspect-square h-auto max-w-[520px] mx-auto shadow-[0_0_120px_rgba(242,165,65,0.35)]" : ""}`}>
            <img src={absFile(e.image_url || t.image)} alt={e.name} className="w-full h-full object-cover" />
          </div>
          {slug === "wedding" && <FloralVine className="absolute -bottom-8 left-0 w-full" color={t.accent} />}
        </div>
      </section>

      <section className="relative max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24">
        <div className="grid md:grid-cols-12 gap-6" style={{ "--notch": t.bg }}>
          <div className="md:col-span-8 p-8 md:p-10 relative" style={{ background: t.soft }} data-testid="event-details-card">
            <div className="kicker" style={{ color: t.accent }}>The details</div>
            <div className="grid sm:grid-cols-2 gap-8 mt-6">
              <Meta label="Date">{niceDate}</Meta>
              <Meta label="Timing">{[e.start_time, e.end_time].filter(Boolean).join(" – ") || "Timings coming soon"}</Meta>
              <Meta label="Venue">{e.venue}{e.venue_address && <span className="block text-sm opacity-70 mt-1">{e.venue_address}</span>}</Meta>
              <Meta label="Dress code">{e.dress_code}<Link to={`/dress-code#${slug}`} className="block kicker mt-2 link-sweep w-fit" style={{ color: t.accent }} data-testid="event-lookbook-link">See the lookbook →</Link></Meta>
              {names.length > 0 && <div className="sm:col-span-2" data-testid="event-invited-names"><Meta label="From your family">{names.join(", ")}</Meta></div>}
            </div>
            <div className="flex flex-wrap gap-3 mt-10">
              <button data-testid="add-to-calendar" onClick={addToCalendar} className="btn-pop rounded-full px-6 py-3.5 kicker inline-flex items-center gap-2 bg-[#FF6657] text-[#252122]"><CalendarPlus size={15} /> Add to calendar</button>
              {e.maps_url && <a href={e.maps_url} target="_blank" rel="noreferrer" data-testid="event-map-link" className="btn-pop rounded-full px-6 py-3.5 kicker inline-flex items-center gap-2 bg-[#FFF9F0] text-[#252122]"><MapPin size={15} /> Open map</a>}
            </div>
          </div>
          <div className="md:col-span-4 p-8 md:p-10 flex flex-col justify-between" style={{ background: t.accent, color: onLight ? "#FFF9F0" : t.bg }}>
            <div className="kicker">The vibe</div>
            <p className="font-display italic text-3xl leading-tight mt-4">{e.theme}</p>
            <div className="flex gap-2 mt-8">{(e.dress_code_colors?.length ? e.dress_code_colors : t.palette).map((c) => <span key={c} className="w-9 h-9 rounded-full border-2" style={{ background: c, borderColor: onLight ? "#FFF9F0" : t.bg }} />)}</div>
          </div>
        </div>
        <div className="grid md:grid-cols-12 gap-10 mt-16">
          <p className="md:col-span-7 font-display text-2xl md:text-4xl leading-snug">{e.description}</p>
          {e.instructions && (
            <div className="md:col-span-5 border-l-4 pl-6" style={{ borderColor: t.accent }}>
              <div className="font-hand text-2xl" style={{ color: t.accent }}>good to know</div>
              <p className="mt-2 opacity-90 text-lg">{e.instructions}</p>
            </div>
          )}
        </div>
      </section>

      {next && next.slug !== slug && (
        <Link to={`/celebrations/${next.slug}`} data-testid="next-event-link" className="block relative group" style={{ background: themeFor(next.slug).bg, color: themeFor(next.slug).fg }}>
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 flex items-center justify-between gap-6">
            <div>
              <div className="kicker flex items-center gap-2"><Asterisk size={12} /> Up next</div>
              <div className={`text-4xl md:text-6xl mt-2 ${themeFor(next.slug).titleClass}`}>{next.name}</div>
            </div>
            <ArrowRight size={40} className="group-hover:translate-x-2 transition-transform" />
          </div>
        </Link>
      )}
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="kicker opacity-60 mb-2">{label}</div>
      <div className="text-lg font-semibold">{children}</div>
    </div>
  );
}
